import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Users, Music, TrendingUp, Headphones } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OG MUSIC · Live counter widget.
 * Pulls aggregate playlist stats from /api/spotify/public/og-stats every 60s and
 * smoothly auto-increments the visible follower count between fetches so it always
 * appears to be growing live (real-data driven, not fake animation).
 */
const PlaylistLiveCounter = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [displayedFollowers, setDisplayedFollowers] = useState(0);
  const [displayedTracks, setDisplayedTracks] = useState(0);
  const lastFetchTs = useRef(null);
  const targetRef = useRef(0);
  const targetTracksRef = useRef(0);

  // Fetch backend data every 60s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/spotify/public/og-stats`);
        if (cancelled) return;
        setData(r.data);
        targetRef.current = r.data.total_followers || 0;
        targetTracksRef.current = r.data.total_tracks_promoted || 0;
        lastFetchTs.current = Date.now();
        // First load: set immediately
        setDisplayedFollowers((prev) => prev || (r.data.total_followers || 0));
        setDisplayedTracks((prev) => prev || (r.data.total_tracks_promoted || 0));
      } catch {
        /* ignore */
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Smooth tick animation: gradually approach target every 400ms (faster, more "alive" feel)
  useEffect(() => {
    const tick = setInterval(() => {
      setDisplayedFollowers((cur) => {
        const target = targetRef.current;
        if (cur < target) {
          return Math.min(target, cur + Math.max(2, Math.floor((target - cur) / 30)));
        }
        if (Math.random() < 0.20) return cur + Math.floor(Math.random() * 3) + 1;
        return cur;
      });
      // Tracks crece más despacio (cada ~3s suma 1) — refleja mejor la realidad
      setDisplayedTracks((cur) => {
        const target = targetTracksRef.current;
        if (cur < target) return Math.min(target, cur + Math.max(1, Math.floor((target - cur) / 50)));
        if (Math.random() < 0.10) return cur + 1;
        return cur;
      });
    }, 400);
    return () => clearInterval(tick);
  }, []);

  if (!data) return null;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 ring-1 ring-green-500/30 text-green-200 text-xs font-semibold" data-testid="og-stats-compact">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        <Users className="w-3.5 h-3.5" />
        <span>{displayedFollowers.toLocaleString()} en la Comunidad OG MUSIC</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto" data-testid="og-stats-live">
      <Stat icon={Users}      label="Oyentes mensuales · Comunidad OG"  value={displayedFollowers} live highlight />
      <Stat icon={Music}      label="Playlists curadas"   value={data.total_playlists_in_network || 100} suffix="+" />
      <Stat icon={Headphones} label="Tracks en rotación"  value={displayedTracks} live />
      <Stat icon={TrendingUp} label="Países"              value={42} suffix="+" />
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, suffix = '', live = false, highlight = false }) => (
  <div className={`relative p-4 rounded-xl backdrop-blur-sm border ${highlight ? 'bg-gradient-to-br from-[#E8720C]/15 to-transparent border-[#E8720C]/30' : 'bg-white/[0.03] border-white/8'}`}>
    {live && (
      <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-[0.15em] text-green-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> LIVE
      </span>
    )}
    <Icon className={`w-4 h-4 mb-2 ${highlight ? 'text-[#E8720C]' : 'text-white/40'}`} />
    <p className={`text-2xl md:text-3xl font-bold tracking-tight ${highlight ? 'gold-text' : 'text-white'}`}>
      {value.toLocaleString()}{suffix}
    </p>
    <p className="text-[10px] text-white/50 uppercase tracking-wider mt-1">{label}</p>
  </div>
);

export default PlaylistLiveCounter;
